// Home-dashboard layout for widget tiles: order, per-tile size and hidden tiles.
// Saved per user in this browser (localStorage) — the same place the rest of the home
// screen keeps its per-device preferences. Moving it server-side later only means
// swapping load/save; the shape below is the contract.

export type TileSize = 'sm' | 'wide' | 'tall' | 'lg';

export const TILE_SIZE_CYCLE: TileSize[] = ['sm', 'wide', 'tall', 'lg'];
export const TILE_SIZE_LABEL: Record<TileSize, string> = { sm: 'Small', wide: 'Wide', tall: 'Tall', lg: 'Large' };

export interface HomeLayout {
  /** Tile ids in display order; ids not listed are appended in install order. */
  order: string[];
  sizes: Record<string, TileSize>;
  hidden: string[];
}

const LAYOUT_PREFIX = 'wg_home_layout:';
export const EMPTY_LAYOUT: HomeLayout = { order: [], sizes: {}, hidden: [] };

function isTileSize(value: unknown): value is TileSize {
  return typeof value === 'string' && (TILE_SIZE_CYCLE as string[]).includes(value);
}

export function loadHomeLayout(userKey: string): HomeLayout {
  try {
    const raw = localStorage.getItem(`${LAYOUT_PREFIX}${userKey}`);
    if (!raw) return EMPTY_LAYOUT;
    const parsed = JSON.parse(raw) as Partial<HomeLayout>;
    const sizes: Record<string, TileSize> = {};
    for (const [id, size] of Object.entries(parsed.sizes ?? {})) if (isTileSize(size)) sizes[id] = size;
    return {
      order: Array.isArray(parsed.order) ? parsed.order.filter((id): id is string => typeof id === 'string') : [],
      sizes,
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden.filter((id): id is string => typeof id === 'string') : [],
    };
  } catch {
    return EMPTY_LAYOUT;
  }
}

export function saveHomeLayout(userKey: string, layout: HomeLayout): void {
  try { localStorage.setItem(`${LAYOUT_PREFIX}${userKey}`, JSON.stringify(layout)); } catch { /* quota or private mode — layout just won't persist */ }
}

/** Splits installed tile widgets into the ordered visible list and the hidden list. */
export function arrangeTiles<T extends { id: string }>(widgets: T[], layout: HomeLayout): { visible: T[]; hidden: T[] } {
  const rank = new Map(layout.order.map((id, index) => [id, index]));
  const ordered = [...widgets].sort((a, b) => {
    const ra = rank.get(a.id); const rb = rank.get(b.id);
    if (ra === undefined && rb === undefined) return 0;          // keep install order
    if (ra === undefined) return 1;
    if (rb === undefined) return -1;
    return ra - rb;
  });
  const hiddenSet = new Set(layout.hidden);
  return { visible: ordered.filter(w => !hiddenSet.has(w.id)), hidden: ordered.filter(w => hiddenSet.has(w.id)) };
}

export function tileSize(layout: HomeLayout, id: string): TileSize {
  return layout.sizes[id] ?? 'sm';
}

/** Moves `id` by `delta` positions within `visibleIds` and records the full resulting order. */
export function moveTile(layout: HomeLayout, visibleIds: string[], id: string, delta: number): HomeLayout {
  const from = visibleIds.indexOf(id);
  if (from < 0) return layout;
  const to = Math.max(0, Math.min(visibleIds.length - 1, from + delta));
  if (to === from) return layout;
  const next = [...visibleIds];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return { ...layout, order: next };
}

/** Drops `id` in front of `targetId` (drag-and-drop). */
export function placeTileBefore(layout: HomeLayout, visibleIds: string[], id: string, targetId: string): HomeLayout {
  if (id === targetId || !visibleIds.includes(id) || !visibleIds.includes(targetId)) return layout;
  const without = visibleIds.filter(x => x !== id);
  const at = without.indexOf(targetId);
  without.splice(at, 0, id);
  return { ...layout, order: without };
}

export function cycleTileSize(layout: HomeLayout, id: string): HomeLayout {
  const current = tileSize(layout, id);
  const next = TILE_SIZE_CYCLE[(TILE_SIZE_CYCLE.indexOf(current) + 1) % TILE_SIZE_CYCLE.length];
  return { ...layout, sizes: { ...layout.sizes, [id]: next } };
}

export function setTileHidden(layout: HomeLayout, id: string, hidden: boolean): HomeLayout {
  const without = layout.hidden.filter(x => x !== id);
  return { ...layout, hidden: hidden ? [...without, id] : without };
}

/** Drop layout entries for widgets that are no longer installed. */
export function pruneLayout(layout: HomeLayout, installedIds: string[]): HomeLayout {
  const keep = new Set(installedIds);
  const sizes: Record<string, TileSize> = {};
  for (const [id, size] of Object.entries(layout.sizes)) if (keep.has(id)) sizes[id] = size;
  return {
    order: layout.order.filter(id => keep.has(id)),
    sizes,
    hidden: layout.hidden.filter(id => keep.has(id)),
  };
}
